import { formatPesoAmount } from './money';
import { normalizeQuantity } from './quantity';
import { isScaleReadingLine, lineMatchesPrice } from './posScaleReadingLines';
import { isScaleInputPrice } from './scalePresentation';
import { getFormatOfferId, getSaleOptionId, isFormatOfferSelectable } from './formatOffers';

const normalizePositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const formatSlotMoney = (value) => `$${formatPesoAmount(Number(value || 0))}`;

export const formatPosSlotQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

export const buildPosSlotLayoutScopeKey = (branchId, cashRegisterId) => {
  const resolvedBranchId = normalizePositiveInteger(branchId);
  const resolvedCashRegisterId = normalizePositiveInteger(cashRegisterId);
  if (!resolvedBranchId || !resolvedCashRegisterId) return '';
  return `${resolvedBranchId}:${resolvedCashRegisterId}`;
};

export const normalizePosSlotAssignment = (value) => {
  const varietyId = normalizePositiveInteger(value?.varietyId);
  const varietyPriceId = normalizePositiveInteger(value?.varietyPriceId);
  if (!varietyId || !varietyPriceId) return null;
  const formatOfferId = getFormatOfferId(value);
  if (value?.formatOfferId != null && !formatOfferId) return null;
  return { varietyId, varietyPriceId, ...(formatOfferId ? { formatOfferId } : {}) };
};

const normalizeLayoutEntries = (entries) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .map(normalizePosSlotAssignment)
    .filter(Boolean)
    .filter((entry) => {
      const key = `${entry.varietyId}:${getSaleOptionId(entry)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const normalizePosSlotLayouts = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  const normalized = {};
  Object.entries(value).forEach(([scopeKey, entries]) => {
    const [branchIdRaw, cashRegisterIdRaw] = String(scopeKey || '').split(':');
    const normalizedScopeKey = buildPosSlotLayoutScopeKey(branchIdRaw, cashRegisterIdRaw);
    if (!normalizedScopeKey) return;
    normalized[normalizedScopeKey] = normalizeLayoutEntries(entries);
  });
  return normalized;
};

export const getPosSlotLayout = (layouts, branchId, cashRegisterId) => {
  const scopeKey = buildPosSlotLayoutScopeKey(branchId, cashRegisterId);
  if (!scopeKey) return [];
  const normalizedLayouts = normalizePosSlotLayouts(layouts);
  return Array.isArray(normalizedLayouts[scopeKey]) ? normalizedLayouts[scopeKey] : [];
};

export const setPosSlotLayout = (layouts, branchId, cashRegisterId, nextLayout) => {
  const scopeKey = buildPosSlotLayoutScopeKey(branchId, cashRegisterId);
  const normalizedLayouts = normalizePosSlotLayouts(layouts);
  if (!scopeKey) return normalizedLayouts;
  return {
    ...normalizedLayouts,
    [scopeKey]: normalizeLayoutEntries(nextLayout),
  };
};

export const removePosSlotAt = (layout, slotIndex) => (
  (Array.isArray(layout) ? layout : []).filter((_, index) => index !== slotIndex)
);

export const movePosSlot = (layout, fromIndex, toIndex) => {
  const entries = Array.isArray(layout) ? [...layout] : [];
  if (
    fromIndex === toIndex
    || fromIndex < 0
    || toIndex < 0
    || fromIndex >= entries.length
    || toIndex >= entries.length
  ) {
    return entries;
  }
  const [moved] = entries.splice(fromIndex, 1);
  entries.splice(toIndex, 0, moved);
  return entries;
};

export const findPosSlotDefinition = (products, slot) => {
  const normalizedSlot = normalizePosSlotAssignment(slot);
  if (!normalizedSlot) return null;

  for (const product of products || []) {
    for (const variety of product?.Varieties || []) {
      if (Number(variety?.id) !== Number(normalizedSlot.varietyId)) continue;
      const priceIndex = (variety?.VarietyPrices || []).findIndex(
        (price) => Number(price?.id) === getSaleOptionId(normalizedSlot)
      );
      return {
        product,
        variety,
        price: priceIndex >= 0 ? variety.VarietyPrices[priceIndex] : null,
        priceIndex,
        slot: normalizedSlot,
      };
    }
  }

  return null;
};

export const buildPosSlotPickerEntries = ({
  products = [],
  isPriceAllowedByRegister,
  resolveDisplayPrice,
}) => (
  (products || [])
    .flatMap((product) => (
      (product?.Varieties || []).flatMap((variety) => (
        (variety?.VarietyPrices || []).map((price, priceIndex) => ({
          product,
          variety,
          price,
          priceIndex,
        }))
      ))
    ))
    .filter(({ price }) => {
      const isSellable = price?.price !== null && price?.price !== undefined;
      if (!isSellable || !isFormatOfferSelectable(price)) return false;
      return typeof isPriceAllowedByRegister === 'function'
        ? isPriceAllowedByRegister(price)
        : true;
    })
    .map(({ product, variety, price, priceIndex }) => {
      const display = typeof resolveDisplayPrice === 'function'
        ? resolveDisplayPrice(variety, price, 0)
        : null;
      const displayPrice = Number.isFinite(Number(display?.price))
        ? Number(display.price)
        : Number(price?.price || 0);
      const description = String(price?.description || '').trim();
      return {
        key: `${variety?.id || 'variety'}:${price?.id || priceIndex}`,
        product,
        variety,
        price,
        productName: product?.productName || 'Producto',
        varietyName: variety?.varietyName || 'Variedad',
        metricUnit: price?.metricUnit || 'Precio',
        description,
        detailLabel: [price?.metricUnit, description].filter(Boolean).join(' · '),
        priceLabel: formatSlotMoney(displayPrice),
        imageSrc: price?.priceImage
          || variety?.varietyImage
          || product?.productImage
          || 'https://placehold.co/200',
        varietyId: variety?.id,
        varietyPriceId: price?.parentVarietyPriceId ?? price?.id,
        ...(getFormatOfferId(price) ? { formatOfferId: getFormatOfferId(price) } : {}),
        priceIndex,
        soldOut: Boolean(price?.isSoldOut),
        useScaleWeight: isScaleInputPrice(price),
      };
    })
    .sort((left, right) => (
      `${left.productName} ${left.varietyName} ${left.metricUnit}`
        .localeCompare(`${right.productName} ${right.varietyName} ${right.metricUnit}`)
    ))
);

const getScaleReadingSummary = (scaleReadingLines, variety, price, priceIndex) => {
  const matchingLines = (Array.isArray(scaleReadingLines) ? scaleReadingLines : []).filter(
    (line) => isScaleReadingLine(line) && lineMatchesPrice(
      line,
      variety?.id,
      priceIndex,
      price
    )
  );
  return {
    scaleReadingCount: matchingLines.length,
    scaleReadingQuantity: matchingLines.reduce(
      (sum, line) => normalizeQuantity(sum + normalizeQuantity(line?.quantity, 3), 3),
      0
    ),
  };
};

export const buildResolvedPosSlots = ({
  products = [],
  slotLayout = [],
  quantities = {},
  discounts = {},
  scaleReadingLines = [],
  isPriceAllowedByRegister,
  resolveDisplayPrice,
}) => (
  (slotLayout || []).map((slot, slotIndex) => {
    const resolved = findPosSlotDefinition(products, slot);
    if (!resolved) {
      return {
        key: `missing:${slotIndex}`,
        slotIndex,
        slot: normalizePosSlotAssignment(slot) || slot,
        disabled: true,
        invalid: true,
        invalidReason: 'La variedad o el precio ya no existen.',
        productName: 'Precio eliminado',
        varietyName: '',
        metricUnit: 'No disponible',
        priceLabel: '--',
        quantityLabel: '0',
        currentQuantity: 0,
      };
    }

    const { product, variety, price, priceIndex } = resolved;
    const unitDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
    const display = price && typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, unitDiscount)
      : null;
    const displayPrice = Number.isFinite(Number(display?.price))
      ? Number(display.price)
      : Number(price?.price || 0);
    const matrixQuantity = priceIndex >= 0
      ? normalizeQuantity(quantities?.[variety?.id]?.[priceIndex] || 0)
      : 0;
    const scaleSummary = getScaleReadingSummary(
      scaleReadingLines,
      variety,
      price,
      priceIndex
    );
    const currentQuantity = normalizeQuantity(
      matrixQuantity + scaleSummary.scaleReadingQuantity,
      3
    );
    const allowedByRegister = price && typeof isPriceAllowedByRegister === 'function'
      ? isPriceAllowedByRegister(price)
      : Boolean(price);
    const disabled = !price || !allowedByRegister || Boolean(price?.isSoldOut) || !isFormatOfferSelectable(price);
    let invalidReason = '';
    if (price?.formatOfferExpired) {
      invalidReason = 'La oferta finalizó. Selecciona un precio vigente.';
    } else if (price?.formatOfferReplaced) {
      invalidReason = 'Este formato ahora se vende solo en oferta.';
    } else if (!price) {
      invalidReason = 'El precio configurado ya no existe.';
    } else if (!allowedByRegister) {
      invalidReason = 'Este precio no esta permitido para la caja actual.';
    } else if (price?.isSoldOut) {
      invalidReason = 'Precio agotado.';
    }
    const useScaleWeight = isScaleInputPrice(price);

    return {
      key: `${variety?.id || 'variety'}:${price?.id || priceIndex}:${slotIndex}`,
      slotIndex,
      slot: resolved.slot,
      product,
      variety,
      price,
      priceIndex,
      varietyId: variety?.id,
      varietyPriceId: price?.parentVarietyPriceId ?? price?.id,
      ...(getFormatOfferId(price) ? { formatOfferId: getFormatOfferId(price) } : {}),
      productName: product?.productName || 'Producto',
      varietyName: variety?.varietyName || 'Variedad',
      metricUnit: price?.metricUnit || 'Precio',
      description: String(price?.description || '').trim(),
      priceLabel: formatSlotMoney(displayPrice),
      imageSrc: price?.priceImage
        || variety?.varietyImage
        || product?.productImage
        || 'https://placehold.co/200',
      currentQuantity,
      matrixQuantity,
      quantityLabel: formatPosSlotQuantity(currentQuantity),
      disabled,
      invalid: !price || !allowedByRegister,
      invalidReason,
      soldOut: Boolean(price?.isSoldOut),
      unitPrice: displayPrice,
      taxFreeApplied: Boolean(display?.taxFreeApplied),
      useScaleWeight,
      scaleReadingCount: scaleSummary.scaleReadingCount,
      scaleReadingQuantity: scaleSummary.scaleReadingQuantity,
    };
  })
);
